import { filterBy } from "@progress/kendo-data-query";
import { MultiColumnComboBox } from "@progress/kendo-react-dropdowns";
import React, { useEffect, useState } from "react";
import {
  CreateEntryTemplateButton,
  EditDatasetButton,
  EditTemplateButton,
  ViewDatasetButton,
  ViewTemplateButton,
} from "../Buttons/HomepageButtons";
import { allDatasets, allTemplates, userData } from "../Common/Store/CommonSignals";
import { userBelongsToUserGroup, userHasAccessToResource } from "../Utilities/AuthorizationUtil";
import "./Homepage.css";

const UserPermissionText = (props) => {
  //set permission based on user email and shared permission
  if (props.owner.toLowerCase() === props.email) {
    return "Owner";
  } else if (
    props.coOwners.some((co) => {
      // Check individual co-owners
      if (!co.isUserGroup && co.user.toLowerCase() === props.email) {
        return true;
      }
      // Check if user belongs to a co-owner group
      if (co.isUserGroup && co.userGroupId) {
        return userBelongsToUserGroup(props.email, co.userGroupId);
      }
      return false;
    })
  ) {
    return "Co-Owner";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email &&
        sp.permission === "Modify"
      ) {
        return true;
      }
      // Check if user belongs to a group with Modify permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "Modify") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "Modify";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (!sp.isUserGroup && sp.entity.toLowerCase() === props.email && sp.permission === "View") {
        return true;
      }
      // Check if user belongs to a group with View permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "View") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "View Only";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email &&
        sp.permission === "MakeEntry"
      ) {
        return true;
      }
      // Check if user belongs to a group with MakeEntry permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "MakeEntry") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "Make Entry";
  } else if (
    props.sharedPermission.some((sp) => {
      // Check individual permissions
      if (
        !sp.isUserGroup &&
        sp.entity.toLowerCase() === props.email &&
        sp.permission === "ViewAuthorOnly"
      ) {
        return true;
      }
      // Check if user belongs to a group with ViewAuthorOnly permission
      if (sp.isUserGroup && sp.userGroupId && sp.permission === "ViewAuthorOnly") {
        return userBelongsToUserGroup(props.email, sp.userGroupId);
      }
      return false;
    })
  ) {
    return "Make Entry";
  }
};

const searchColumns = [
  {
    field: "label",
    header: "Name",
    width: "12%",
  },
  {
    field: "permission",
    header: "Permission",
    width: "10%",
  },
  {
    field: "type",
    header: "Type",
    width: "8%",
  },
  {
    field: "button",
    width: "8%",
  },
];

const ButtonContent = (props) => {
  const { type } = props.dataItem;

  if (type === "Template") {
    return (
      <div className="align-columns">
        <CreateEntryTemplateButton {...props} />
        <EditTemplateButton {...props} />
        <ViewTemplateButton {...props} />
      </div>
    );
  }

  if (type === "Dataset") {
    return (
      <div className="align-columns">
        <EditDatasetButton {...props} />
        <ViewDatasetButton {...props} />
      </div>
    );
  }

  return <></>;
};

const renderColumnContent = (col, props) => {
  if (col.field === "button") {
    return <ButtonContent {...props} />;
  }

  return props.dataItem[col.field];
};

const itemRender = (li, props) => {
  const children = searchColumns.map((col, i) => {
    const content = renderColumnContent(col, props);

    return (
      <span
        key={col.field || i}
        className="k-table-td"
        style={{
          width: col.width,
        }}
      >
        {content}
      </span>
    );
  });

  return React.cloneElement(
    li,
    {
      ...li.props,
      onClick: null,
    },
    children
  );
};

export const SearchBar = () => {
  const userEmail = userData.value?.userEmail;
  const [searchBarData, setSearchBarData] = useState([]);
  const [filter, setFilter] = React.useState();
  const templates = allTemplates.value || [];
  const datasets = allDatasets.value || [];

  const accessTemplates = templates.filter((template) => {
    return userHasAccessToResource(template, userEmail);
  });

  const userDatasets = datasets.filter((dataset) => {
    return userHasAccessToResource(dataset, userEmail);
  });

  const templateSearch =
    accessTemplates?.map((template) => ({
      ...template,
      label: template.templateName,
      permission: UserPermissionText({ ...template, email: userEmail }),
      type: "Template",
    })) || [];

  const datasetSearch = userDatasets.map((dataset) => ({
    ...dataset,
    label: dataset.datasetName,
    permission: UserPermissionText({ ...dataset, email: userEmail }),
    type: "Dataset",
  }));

  useEffect(() => {
    const tempData = [...templateSearch, ...datasetSearch];
    if (JSON.stringify(tempData) !== JSON.stringify(searchBarData)) {
      setSearchBarData(tempData);
    }
  }, [templateSearch, datasetSearch]);

  const handleFilterChange = (event) => {
    if (event) {
      setFilter(event.filter);
    }
  };

  return (
    <div className={"searchContainer"}>
      <svg
        className="searchIcon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.6393 15.4286H17.8554L24 21.5732L21.5732 24L15.4286 17.8554V16.6393L13.9982 15.2089C12.5196 16.4196 10.6286 17.1429 8.57143 17.1429C3.83571 17.1429 0 13.3071 0 8.57143C0 3.83571 3.83571 0 8.57143 0C13.3071 0 17.1429 3.83571 17.1429 8.57143C17.1429 10.6286 16.4196 12.5196 15.2089 13.9982L16.6393 15.4286ZM8.57138 1.71429C4.78388 1.71429 1.71423 4.78393 1.71423 8.57143C1.71423 12.3589 4.78388 15.4286 8.57138 15.4286C12.3589 15.4286 15.4285 12.3589 15.4285 8.57143C15.4285 4.78393 12.3589 1.71429 8.57138 1.71429Z"
          fill="#00aeef"
        />
      </svg>
      <MultiColumnComboBox
        className="searchColumnBox"
        name="txtHomeSearchBar"
        data={filter ? filterBy(searchBarData, filter) : searchBarData}
        columns={searchColumns}
        filterable={true}
        textField={"label"}
        itemRender={itemRender}
        onFilterChange={handleFilterChange}
        popupSettings={{ className: "k-animation-container-shown", height: "60vh" }}
        placeholder="Search..."
        style={{
          width: "100%",
        }}
      />
    </div>
  );
};
